import React, { createContext, useContext, useState, useEffect } from 'react';
import { Household, HouseholdContextType } from '../types/household';
import { fetchUserHouseholds } from '../api/household';
import { useAuth } from './AuthContext';
import { useNotifications } from '../hooks/useNotifications';

const HouseholdContext = createContext<HouseholdContextType | undefined>(undefined);

export const useHousehold = () => {
  const context = useContext(HouseholdContext);
  if (!context) {
    throw new Error('useHousehold must be used within a HouseholdProvider');
  }
  return context;
};

export const HouseholdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [households, setHouseholds] = useState<Household[]>([]);
  const [activeHouseholdId, setActiveHouseholdId] = useState<string | null>(() => {
    return localStorage.getItem('activeHouseholdId');
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showHouseholdSelector, setShowHouseholdSelector] = useState(false);

  const { user } = useAuth();
  const { showError } = useNotifications();


  useEffect(() => {
    if (user) {
      fetchHouseholdsAndCheck();
    } else {
      // User logged out - reset state
      setHouseholds([]);
      setActiveHouseholdId(null);
      setShowHouseholdSelector(false);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchHouseholdsAndCheck = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const userHouseholds = await fetchUserHouseholds();
      setHouseholds(userHouseholds);

      const activeHouseholds = userHouseholds.filter(h => h.status === 1);

      if (activeHouseholds.length === 0) {
        showError('Nie masz dostępu do żadnego gospodarstwa domowego');
        setActiveHouseholdId(null);
        localStorage.removeItem('activeHouseholdId');
        return;
      }

      // Check if saved household is still valid
      const savedHouseholdId = localStorage.getItem('activeHouseholdId');
      const isValidSavedHousehold = savedHouseholdId &&
        activeHouseholds.some(h => h.id === savedHouseholdId);

      if (isValidSavedHousehold) {
        setActiveHouseholdId(savedHouseholdId);
        setShowHouseholdSelector(false);
        return;
      }

      // If no valid saved household, show selector or auto-select
      if (activeHouseholds.length === 1) {
        const household = activeHouseholds[0];
        setActiveHousehold(household.id);
      } else {
        setActiveHouseholdId(null);
        localStorage.removeItem('activeHouseholdId');
        setShowHouseholdSelector(true);
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Błąd podczas pobierania gospodarstw domowych');
    } finally {
      setIsLoading(false);
    }
  };

  const setActiveHousehold = (householdId: string) => {
    setActiveHouseholdId(householdId);
    localStorage.setItem('activeHouseholdId', householdId);
    setShowHouseholdSelector(false);
  };

  const fetchUserHouseholdsWrapper = async () => {
    await fetchHouseholdsAndCheck();
  };

  return (
    <HouseholdContext.Provider
      value={{
        households,
        activeHouseholdId,
        setActiveHousehold,
        fetchUserHouseholds: fetchUserHouseholdsWrapper,
        isLoading,
        showHouseholdSelector,
        setShowHouseholdSelector,
      }}
    >
      {children}
    </HouseholdContext.Provider>
  );
};